import { useEffect, useRef, useState } from "react";
import type { LiveEvent } from "@saucer/core";

const RECONNECT_DELAY_MS = 4000;
const PING_INTERVAL_MS = 30_000;
const KEEP = 12;

/**
 * The dashboard's window onto its own links being opened. The socket lives in
 * a hibernating Durable Object, so holding it open all day is cheap.
 */
export function useLive(enabled: boolean): LiveEvent[] {
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const socket = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let retry: number | undefined;
    let ping: number | undefined;

    const connect = () => {
      if (closed) return;
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const next = new WebSocket(`${protocol}://${window.location.host}/api/live`);
      socket.current = next;

      next.addEventListener("message", (message) => {
        const payload = JSON.parse(String(message.data)) as
          | { type: "hello"; recent: LiveEvent[] }
          | { type: "event"; event: LiveEvent };
        setEvents((current) =>
          payload.type === "hello"
            ? payload.recent.slice(0, KEEP)
            : [payload.event, ...current].slice(0, KEEP),
        );
      });

      next.addEventListener("close", () => {
        if (!closed) retry = window.setTimeout(connect, RECONNECT_DELAY_MS);
      });

      ping = window.setInterval(() => {
        if (next.readyState === WebSocket.OPEN) next.send("ping");
      }, PING_INTERVAL_MS);
    };

    connect();
    return () => {
      closed = true;
      window.clearTimeout(retry);
      window.clearInterval(ping);
      socket.current?.close();
    };
  }, [enabled]);

  return events;
}
